import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { resolveTailNames } from '@fcom/core/utils';
import { FinnairBoundItem, FinnairItineraryItemFlight } from '@fcom/dapi/api/models';
import { IconButtonSize } from '@fcom/ui-components/components/buttons';

import { isFlight } from '../../../utils/flight.utils';
import { getTails, isBus, isTrain } from '../../../utils/common-booking.utils';
import { cityNameByLocationCodeSelector } from '../../../store';

interface JourneyLeg {
  icon: SvgLibraryIcon;
  transferLocationCode?: string;
}

@Component({
  selector: 'fin-shopping-cart-bound-header',
  standalone: false,
  templateUrl: './shopping-cart-bound-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingCartBoundHeaderComponent {
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly IconButtonSize = IconButtonSize;

  private store$ = inject<Store<BookingAndAppState>>(Store);

  bound = input.required<FinnairBoundItem>();

  openItineraryDetails = output<void>();

  protected cityNameByLocationCode = this.store$.selectSignal(cityNameByLocationCodeSelector);

  protected tailNames = computed(() => resolveTailNames(getTails(this.bound())));

  private flights = computed<FinnairItineraryItemFlight[]>(() => this.bound().itinerary.filter(isFlight));

  protected journeyLegs = computed<JourneyLeg[]>(() => {
    const flights = this.flights();
    return flights.map((flight, index) => ({
      icon: isBus(flight)
        ? SvgLibraryIcon.BUS
        : isTrain(flight)
          ? SvgLibraryIcon.TRAIN
          : SvgLibraryIcon.AIRCRAFT_HORIZONTAL,
      transferLocationCode: index < flights.length - 1 ? flight.arrival.locationCode : undefined,
    }));
  });

  // Counted from the bound's own flights rather than the API's `stops`, so a technical stop the passenger
  // stays aboard for is not counted.
  protected transferCount = computed<number>(() => Math.max(this.flights().length - 1, 0));

  protected stopsLabelKey = computed<string>(() => {
    const transfers = this.transferCount();
    if (!transfers) {
      return 'flightItinerary.direct';
    }
    return transfers === 1 ? 'flightItinerary.stopOver.singular' : 'flightItinerary.stopOver.plural';
  });
}
